import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

type Placement = "top" | "bottom" | "left" | "right";

interface CustomTooltipProps {
  /** Content to show. Prefer ReactNode. If passing HTML string, set `useHtml` = true */
  content: React.ReactNode | string;
  /** If true and content is a string, render it as HTML (allows <ul><li> etc.) */
  useHtml?: boolean;
  /** Where to place the tooltip relative to the icon */
  placement?: Placement;
  /** Maximum width in px */
  maxWidth?: number;
  /** Optional: custom trigger icon (defaults to the rounded "i") */
  triggerIcon?: React.ReactNode;
  /** Optional extra class on the floating tooltip */
  className?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  content,
  useHtml = false,
  placement = "bottom",
  maxWidth = 320,
  triggerIcon,
  className,
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // Compute screen position for the floating box
  const updatePos = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();

    let top = r.bottom + 10;
    let left = r.left + r.width / 2;

    if (placement === "top") {
      top = r.top - 10;
      left = r.left + r.width / 2;
    } else if (placement === "left") {
      top = r.top + r.height / 2;
      left = r.left - 10;
    } else if (placement === "right") {
      top = r.top + r.height / 2;
      left = r.right + 10;
    }

    setPos({ top, left });
  };

  useLayoutEffect(() => {
    if (open) updatePos();
  }, [open, placement]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => updatePos();
    const onResize = () => updatePos();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  // Close when clicking elsewhere
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!triggerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [open]);

  const tooltip = (
    <div
      role="tooltip"
      aria-hidden={!open}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        transform:
          placement === "top"
            ? "translate(-50%, -100%)"
            : placement === "left"
            ? "translate(-100%, -50%)"
            : placement === "right"
            ? "translate(0%, -50%)"
            : "translate(-50%, 0%)",
        zIndex: 9999,
        background: "#fff",
        border: "1px solid #E5E7EB", // light gray
        borderRadius: 10,
        padding: "10px 12px",
        color: "#1F2937", // neutral-800
        fontSize: 13.5,
        lineHeight: 1.45,
        boxShadow: "0 12px 30px rgba(17,24,39,0.08)",
        maxWidth,
        pointerEvents: "auto",
      }}
      className={className}
      // stop clicks inside
      onClick={(e) => e.stopPropagation()}
    >
      {/* Arrow */}
      <span
        style={{
          position: "absolute",
          width: 10,
          height: 10,
          background: "#fff",
          borderLeft: "1px solid #E5E7EB",
          borderTop: "1px solid #E5E7EB",
          transform:
            placement === "top"
              ? "translate(-50%, 50%) rotate(225deg)"
              : placement === "left"
              ? "translate(50%, -50%) rotate(315deg)"
              : placement === "right"
              ? "translate(-50%, -50%) rotate(135deg)"
              : "translate(-50%, -50%) rotate(45deg)",
          top:
            placement === "top"
              ? "100%"
              : placement === "left" || placement === "right"
              ? "50%"
              : 0,
          left:
            placement === "left"
              ? "100%"
              : placement === "right"
              ? 0
              : "50%",
        }}
      />
      {/* Content */}
      {useHtml && typeof content === "string" ? (
        <div
          // You control the string — safe in your app context.
          dangerouslySetInnerHTML={{ __html: content }}
        />
      ) : (
        <div>{content}</div>
      )}
    </div>
  );

  return (
    <span
      ref={triggerRef}
      style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={(e) => {
        e.stopPropagation();
        setOpen(true);
      }}
      onMouseLeave={(e) => {
        e.stopPropagation();
        setOpen(false);
      }}
      onClick={(e) => {
        // mobile/touch toggle
        e.stopPropagation();
        setOpen((v) => !v);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setOpen((v) => !v);
        }
      }}
      tabIndex={0}
      aria-haspopup="dialog"
      aria-expanded={open}
    >
      {/* Trigger icon */}
      {triggerIcon || (
        <span
          style={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            border: "1px solid #C7C9D1",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 600,
            background: "#fff",
            color: "#5B5E6A",
            cursor: "pointer",
            userSelect: "none",
          }}
          aria-label="info"
        >
          i
        </span>
      )}

      {open ? ReactDOM.createPortal(tooltip, document.body) : null}
    </span>
  );
};

export default CustomTooltip;
